import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface MeasuringCupVisualProps {
  maxVolume: number;
  currentVolume: number;
  targetVolume?: number;
  unit?: 'ml' | 'l' | 'cl';
  label?: string;
  mode?: 'stimulus' | 'support';
  hideValue?: boolean;
  liquidType?: 'water' | 'milk' | 'oil' | 'syrup';
}
export function MeasuringCupVisual({
  maxVolume,
  currentVolume,
  targetVolume,
  unit = 'ml',
  label,
  mode = 'support',
  hideValue = false,
  liquidType = 'water'
}: MeasuringCupVisualProps) {
  const computed = useMemo(() => {
    const currentPercent = currentVolume / maxVolume * 100;
    const targetPercent = targetVolume !== undefined ? targetVolume / maxVolume * 100 : null;
    // Calculate scale markings (5 evenly spaced marks)
    const marks: number[] = [];
    const interval = maxVolume / 5;
    for (let i = 0; i <= 5; i++) {
      marks.push(Math.round(i * interval));
    }
    return {
      currentPercent: Math.max(0, Math.min(100, currentPercent)),
      targetPercent: targetPercent !== null ? Math.max(0, Math.min(100, targetPercent)) : null,
      marks
    };
  }, [maxVolume, currentVolume, targetVolume]);
  const liquidColors = {
    water: {
      fill: '#3b82f6',
      shimmer: '#60a5fa',
      stroke: '#2563eb',
      gradient: ['#3b82f6', '#60a5fa', '#3b82f6']
    },
    milk: {
      fill: '#f8fafc',
      shimmer: '#ffffff',
      stroke: '#e2e8f0',
      gradient: ['#f1f5f9', '#ffffff', '#f1f5f9']
    },
    oil: {
      fill: '#eab308',
      shimmer: '#fde047',
      stroke: '#ca8a04',
      gradient: ['#eab308', '#fde047', '#eab308']
    },
    syrup: {
      fill: '#b45309',
      shimmer: '#d97706',
      stroke: '#92400e',
      gradient: ['#b45309', '#d97706', '#b45309']
    }
  };
  const colors = liquidColors[liquidType];
  const cupViz = <div className="flex justify-center items-center" data-id="element-12024">
      <svg width="120" height="180" viewBox="0 0 120 180" className="drop-shadow-sm" data-id="element-12025">
        {/* Cup outline (trapezoid shape) */}
        <defs data-id="element-12026">
          <linearGradient id="glassGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-12027">
            <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.3" data-id="element-12028" />
            <stop offset="50%" stopColor="#bae6fd" stopOpacity="0.2" data-id="element-12029" />
            <stop offset="100%" stopColor="#e0f2fe" stopOpacity="0.3" data-id="element-12030" />
          </linearGradient>
          <linearGradient id="liquidGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-12031">
            <stop offset="0%" stopColor={colors.gradient[0]} stopOpacity="0.8" data-id="element-12032" />
            <stop offset="50%" stopColor={colors.gradient[1]} stopOpacity="0.9" data-id="element-12033" />
            <stop offset="100%" stopColor={colors.gradient[2]} stopOpacity="0.8" data-id="element-12034" />
          </linearGradient>
        </defs>

        {/* Cup body */}
        <path d="M 30 10 L 20 160 L 100 160 L 90 10 Z" fill="url(#glassGradient)" stroke="#64748b" strokeWidth="2" data-id="element-12035" />

        {/* Scale markings */}
        {computed.marks.map((volume, i) => {
        const percent = volume / maxVolume * 100;
        const y = 160 - percent / 100 * 150;
        const leftX = 30 - i / 5 * 10;
        const rightX = 90 + i / 5 * 10;
        return <g key={volume} data-id="element-12036">
              <line x1={leftX} y1={y} x2={leftX + 10} y2={y} stroke="#475569" strokeWidth="1" data-id="element-12037" />
              <line x1={rightX - 10} y1={y} x2={rightX} y2={y} stroke="#475569" strokeWidth="1" data-id="element-12038" />
              <text x={rightX + 5} y={y + 3} fontSize="10" fill="#475569" fontWeight="600" data-id="element-12039">
                {volume}
              </text>
            </g>;
      })}

        {/* Liquid (animated) */}
        <motion.path d={`M ${20 + (160 - (160 - computed.currentPercent / 100 * 150)) * 0.066} ${160 - computed.currentPercent / 100 * 150} 
              L 20 160 
              L 100 160 
              L ${100 - (160 - (160 - computed.currentPercent / 100 * 150)) * 0.066} ${160 - computed.currentPercent / 100 * 150} 
              Z`} fill="url(#liquidGradient)" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-12040" />

        {/* Liquid surface shimmer (Meniscus) */}
        <motion.ellipse cx="60" cy={160 - computed.currentPercent / 100 * 150} rx={40 - (160 - computed.currentPercent / 100 * 150) * 0.066} ry="4" fill={colors.shimmer} stroke={colors.stroke} strokeWidth="1.5" opacity="0.9" data-id="element-12041" />

        {/* Target marker (if provided) */}
        {computed.targetPercent !== null && mode === 'stimulus' && <g data-id="element-12042">
            <line x1={20 + (160 - (160 - computed.targetPercent / 100 * 150)) * 0.066} y1={160 - computed.targetPercent / 100 * 150} x2={100 - (160 - (160 - computed.targetPercent / 100 * 150)) * 0.066} y2={160 - computed.targetPercent / 100 * 150} stroke="#f59e0b" strokeWidth="2" strokeDasharray="4 2" data-id="element-12043" />
            <text x="105" y={160 - computed.targetPercent / 100 * 150 + 4} fontSize="12" fill="#d97706" fontWeight="bold" data-id="element-12044">
              ?
            </text>
          </g>}

        {/* Volume label */}
        {mode === 'support' && !hideValue && <text x="60" y="175" textAnchor="middle" fontSize="12" fill="#1e293b" fontWeight="bold" data-id="element-12045">
            {currentVolume} {unit}
          </text>}
      </svg>
    </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-xs mx-auto" data-id="element-12046">
        {label && <p className="text-sm font-semibold text-slate-700 mb-2 text-center" data-id="element-12047">
            {label}
          </p>}
        {cupViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-cyan-100 overflow-hidden w-full max-w-sm mx-auto shadow-sm" data-id="element-12048">
      <div className="bg-cyan-50 px-4 py-3 border-b border-cyan-100" data-id="element-12049">
        <h3 className="text-sm font-bold text-cyan-900 text-center" data-id="element-12050">
          {label || 'Maatbeker'}
        </h3>
      </div>

      <div className="p-4 bg-white" data-id="element-12051">{cupViz}</div>

      <div className="bg-cyan-50/50 p-4 border-t border-cyan-50" data-id="element-12052">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-12053">
          Er zit nu{' '}
          <strong data-id="element-12054">
            {currentVolume} {unit}
          </strong>{' '}
          in de beker. Kijk naar de bovenkant van de blauwe vloeistof om het
          volume af te lezen.
        </p>
      </div>
    </div>;
}